import { useEffect } from 'react';
import { useParams, useLocation, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { getArticleById, getRelatedArticles } from '@/mocks/articles';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  const articleId = id ? parseInt(id, 10) : 0;
  const article = getArticleById(articleId);
  const relatedArticles = article ? getRelatedArticles(article.id, 3) : [];

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  if (!article) {
    return (
      <>
        <Navbar />
        <div className="min-h-screen flex items-center justify-center pt-16">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-foreground-900">文章不存在</h1>
            <Link to="/" className="mt-4 inline-block text-primary-500 hover:text-primary-600 cursor-pointer">返回首页</Link>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  const breadcrumbItems = [
    { label: '首页', href: '/' },
    { label: article.categoryName, href: `/category/${article.category}` },
    { label: article.title },
  ];

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    image: article.image,
    datePublished: article.date,
    author: {
      '@type': 'Person',
      name: article.author,
    },
    publisher: {
      '@type': 'Organization',
      name: '欧美大片推荐',
    },
  };

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(articleSchema)}</script>
      <Navbar />
      <main className="pt-20 md:pt-24 pb-14 md:pb-20">
        <article className="max-w-4xl mx-auto px-4 md:px-6">
          <div className="mb-6">
            <Breadcrumb items={breadcrumbItems} />
          </div>

          <div className="mb-6 md:mb-8">
            <div className="flex items-center gap-3 mb-3 flex-wrap">
              <span className="text-xs px-2.5 py-1 rounded-full bg-secondary-100 text-secondary-700 font-medium">
                {article.categoryName}
              </span>
              <span className="text-sm text-foreground-500">{article.date}</span>
              <span className="text-sm text-foreground-400">·</span>
              <span className="text-sm text-foreground-500">{article.author}</span>
            </div>
            <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold font-heading text-foreground-900 leading-tight">
              {article.title}
            </h1>
            {article.tags && (
              <div className="flex flex-wrap gap-2 mt-4">
                {article.tags.map((tag) => (
                  <span key={tag} className="text-xs px-2.5 py-1 rounded-full bg-background-100 text-foreground-600 border border-background-200/70">
                    #{tag}
                  </span>
                ))}
              </div>
            )}
          </div>

          <div className="mb-8 md:mb-10 rounded-lg overflow-hidden aspect-[16/9]">
            <img
              src={article.image}
              alt={article.title}
              title={article.title}
              className="w-full h-full object-cover"
            />
          </div>

          <div
            className="prose-custom text-foreground-800 leading-relaxed"
            dangerouslySetInnerHTML={{ __html: article.content }}
          />

          {relatedArticles.length > 0 && (
            <div className="mt-14 md:mt-20 pt-10 border-t border-background-200/70">
              <h3 className="text-lg md:text-xl font-bold font-heading text-foreground-900 mb-6">
                相关<span className="text-primary-500">推荐</span>
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
                {relatedArticles.map((related) => (
                  <Link
                    key={related.id}
                    to={`/article/${related.id}`}
                    className="group block bg-background-50 rounded-lg border border-background-200/70 overflow-hidden hover:border-primary-300 transition-all duration-300 cursor-pointer"
                  >
                    <div className="aspect-[16/10] overflow-hidden">
                      <img
                        src={related.image}
                        alt={related.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                    <div className="p-4">
                      <span className="text-xs text-foreground-400">{related.date}</span>
                      <h4 className="text-sm font-bold font-heading text-foreground-900 group-hover:text-primary-500 transition-colors duration-200 line-clamp-2 mt-1 leading-snug">
                        {related.title}
                      </h4>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          )}
        </article>
      </main>
      <Footer />
    </>
  );
}